import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { UserProfile } from "@/types/user";
import { ChatMessage, ChatPreview, ChatType } from "../types";
import { useToast } from "@/hooks/use-toast";
import { newChat } from "../services/chatPreviewService";
import { newChatParticipants } from "../services/chatParticipants";

export function useCreateChat(
  user: UserProfile | null,
  chatPreviews: ChatPreview[],
  setChatPreviews: React.Dispatch<React.SetStateAction<ChatPreview[]>>,
  setSelectedChatId: React.Dispatch<React.SetStateAction<string | undefined>>,
  setRecipient: React.Dispatch<React.SetStateAction<UserProfile | null>>,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
) {
  const { toast } = useToast();

  // Function to create a new group chat
  const handleCreateGroupChat = async (
    name: string,
    members: UserProfile[]
  ) => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Utilisateur non connecté",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create the group chat via chatService
      const participantIds = [user.id, ...members.map(m => m.id)];
      const newChatResponse = await newChat("group", name);
      if (!newChatResponse || !newChatResponse.id) {
        throw new Error("Échec de la création du chat");
      }

      await newChatParticipants(newChatResponse.id, user.id, true);
      // Add participants to the chat
      for (const member of members) {
        await newChatParticipants(newChatResponse.id, member.id);
      }

      // Create the group chat preview
      const newGroupChat: ChatPreview = {
        id: newChatResponse.id,
        user: members[0],
        lastMessage: "Groupe créé",
        timestamp: new Date(),
        unread: false,
        type: "group" as ChatType,
        groupName: name,
        members: members,
      };

      // Update state
      setChatPreviews((prev) => [newGroupChat, ...prev]);
      setSelectedChatId(newChatResponse.id);
      setRecipient(members[0]);

      // Initialize with empty messages
      setMessages([]);

      toast({
        title: "Groupe créé",
        description: `Vous avez créé le groupe ${name} avec ${members.length} membres`,
      });
    } catch (error) {
      console.error("Erreur lors de la création du groupe :", error);
      toast({
        title: "Erreur",
        description: "Impossible de créer le groupe. Veuillez réessayer.",
        variant: "destructive",
      });
    }
  };

  // Function to create a new individual chat
  const handleCreateNewChat = async (targetUser: UserProfile) => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Utilisateur non connecté",
        variant: "destructive",
      });
      return;
    }
    try {
      // Create the chat via chatService
      const newChatResponse = await newChat("individual", "");
      if (!newChatResponse || !newChatResponse.id) {
        throw new Error("Échec de la création du chat");
      }

      // Add participants to the chat
      await newChatParticipants(newChatResponse.id, user.id, true);
      await newChatParticipants(newChatResponse.id, targetUser.id);

      // Create chat preview
      const chatPreview: ChatPreview = {
        id: newChatResponse.id,
        user: targetUser,
        lastMessage: "Start a conversation...",
        timestamp: new Date(),
        unread: false,
        type: "individual",
      };

      setChatPreviews((prev) => [chatPreview, ...prev]);
      setSelectedChatId(chatPreview.id);
      setRecipient(chatPreview.user);
      setMessages([]);
    } catch (error) {
      console.error("Erreur lors de la création du chat :", error);
      toast({
        title: "Erreur",
        description: "Impossible de créer le chat. Veuillez réessayer.",
        variant: "destructive",
      });
    }
  };

  return {
    handleCreateGroupChat,
    handleCreateNewChat,
  };
}
